// src/hooks/assistant/useSheetsMode.ts
// Hook for handling sheets mode (two-way AI copilot) in the assistant

import { useState, useCallback, useMemo, useEffect, useRef } from "react";
import { useSheetsStore, useTickerValidation } from "@/features/sheets";
import type { Sheet } from "@/features/sheets";
import {
  getSheetsAIService,
  hasSheetsAICapability,
  type SheetGenerationSpec,
} from "@/services/sheets/SheetsAIService";
import {
  buildSheetContext,
  isSummaryRow,
  findTickerColumn,
  type SheetContext,
} from "@/services/sheets/SheetContextBuilder";
import { colIndexToLetter, isLikelyTicker } from "@/features/sheets/constants";
import { extractLiveFormulas } from "@/features/sheets/utils/formulaEngine";
import { fetchLiveValue } from "@/features/sheets/utils/liveDataService";
import type {
  SheetAction,
  SheetAIResponse,
  AddColumnsAction,
  AddRowsAction,
  EditCellsAction,
  DeleteRowsAction,
  DeleteColumnsAction,
  CreateSheetAction,
} from "@/services/sheets/SheetActions";
import { getActionSummary, isModifyingAction } from "@/services/sheets/SheetActions";
import type { ChatMessage } from "@/services/assistant/AssistantService";

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

export interface SheetsState {
  messages: ChatMessage[];
  isLoading: boolean;

  /** Pending AI response with actions waiting for confirmation */
  pendingResponse: SheetAIResponse | null;

  /** Active sheet context (for two-way interaction) */
  activeSheetContext: SheetContext | null;

  /** Last modified sheet info */
  lastModifiedSheet: { sheetId: string; action: string } | null;

  /** Error message if any */
  error: string | null;

  /** Whether the AI service is available */
  hasCapability: boolean;

  /** Conversation history for multi-turn context */
  conversationHistory: Array<{ role: string; content: string }>;

  // Legacy compatibility
  pendingSpec: SheetGenerationSpec | null;
  lastCreatedSheet: { sheetId: string; sheet: Sheet } | null;
}

export interface SheetsActions {
  /** Send a message to the AI copilot */
  sendMessage: (content: string) => Promise<void>;

  /** Confirm and execute all pending actions */
  confirmActions: () => void;

  /** Discard all pending actions */
  discardActions: () => void;

  /** Execute a single action by index */
  executeAction: (actionIndex: number) => void;

  /** Skip a single action by index */
  skipAction: (actionIndex: number) => void;

  /** Refresh the active sheet context */
  refreshSheetContext: () => void;

  /** Reset the entire conversation */
  reset: () => void;

  /** Clear just the conversation messages */
  clearConversation: () => void;

  // Legacy compatibility
  confirmSheet: () => { sheetId: string; sheet: Sheet } | null;
  discardSheet: () => void;
  modifyPendingSpec: (modification: string) => Promise<void>;
}

// ─────────────────────────────────────────────────────────────────────────────
// Hook Implementation
// ─────────────────────────────────────────────────────────────────────────────

export function useSheetsMode(): [SheetsState, SheetsActions] {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [pendingResponse, setPendingResponse] = useState<SheetAIResponse | null>(null);
  const [lastModifiedSheet, setLastModifiedSheet] = useState<{
    sheetId: string;
    action: string;
  } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [conversationHistory, setConversationHistory] = useState<
    Array<{ role: string; content: string }>
  >([]);
  const [activeSheetContext, setActiveSheetContext] = useState<SheetContext | null>(null);
  const [hasCapability, setHasCapability] = useState(true); // Default to true, check on mount

  // Check AI capability on mount
  useEffect(() => {
    let cancelled = false;
    hasSheetsAICapability().then((available) => {
      if (!cancelled) {
        setHasCapability(available);
      }
    }).catch(() => {
      if (!cancelled) {
        setHasCapability(false);
      }
    });
    return () => { cancelled = true; };
  }, []);

  // Get token catalog for ticker validation and categories
  const { knownTickers, categoriesWithTickers } = useTickerValidation();

  // Get active sheet from store
  const activeSheetId = useSheetsStore((s) => s.activeSheetId);
  const sheets = useSheetsStore((s) => s.sheets);
  const activeSheet = activeSheetId
    ? sheets.find((s) => s.id === activeSheetId)
    : null;

  // Store actions
  const updateCell = useSheetsStore((s) => s.updateCell);
  const updateCells = useSheetsStore((s) => s.updateCells);
  const setCellFormat = useSheetsStore((s) => s.setCellFormat);
  const addRow = useSheetsStore((s) => s.addRow);
  const deleteRow = useSheetsStore((s) => s.deleteRow);
  const addColumn = useSheetsStore((s) => s.addColumn);
  const deleteColumn = useSheetsStore((s) => s.deleteColumn);

  // Track live cache for resolved LIVE() formulas
  const liveCacheRef = useRef<Map<string, number | null>>(new Map());

  // Fetch live values for a sheet and build cache
  const fetchLiveValuesForSheet = useCallback(async (sheet: Sheet): Promise<Map<string, number | null>> => {
    const liveFormulas = extractLiveFormulas(sheet.data);
    const cache = new Map<string, number | null>();

    if (liveFormulas.length === 0) {
      return cache;
    }

    // Fetch all live values in parallel
    const fetchPromises = liveFormulas.map(async ({ ticker, metric }) => {
      const cacheKey = `${ticker}:${metric}`;
      if (!cache.has(cacheKey)) {
        const value = await fetchLiveValue(ticker, metric);
        cache.set(cacheKey, value);
      }
    });

    await Promise.all(fetchPromises);
    return cache;
  }, []);

  // Update context when active sheet changes
  // Using specific properties to avoid rebuilding context on every render
  useEffect(() => {
    if (activeSheet) {
      // Fetch live values first, then build context with resolved values
      fetchLiveValuesForSheet(activeSheet).then((liveCache) => {
        liveCacheRef.current = liveCache;
        const context = buildSheetContext(activeSheet, liveCache, knownTickers);
        setActiveSheetContext(context);
      });
    } else {
      setActiveSheetContext(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSheet?.id, activeSheet?.updatedAt, fetchLiveValuesForSheet, knownTickers]);

  // Send message to AI
  const sendMessage = useCallback(
    async (content: string) => {
      if (!content.trim() || isLoading) return;

      const userMessage: ChatMessage = {
        id: crypto.randomUUID(),
        role: "user",
        content: content.trim(),
      };

      setMessages((prev) => [...prev, userMessage]);
      setIsLoading(true);
      setError(null);
      setPendingResponse(null);

      // Add to conversation history
      const newHistory = [
        ...conversationHistory,
        { role: "user", content: content.trim() },
      ];

      try {
        const service = getSheetsAIService();
        const response = await service.processMessage(
          content,
          activeSheetContext || undefined,
          newHistory.slice(-10), // Keep last 10 messages for context
          categoriesWithTickers
        );

        setPendingResponse(response);

        // Build assistant message from response
        const assistantContent =
          response.conversationalText ||
          response.actions.map((a) => getActionSummary(a)).join("\n\n");

        const assistantMessage: ChatMessage = {
          id: crypto.randomUUID(),
          role: "assistant",
          content: assistantContent,
        };

        setMessages((prev) => [...prev, assistantMessage]);
        setConversationHistory([
          ...newHistory,
          { role: "assistant", content: assistantContent },
        ]);
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : "Failed to process request";
        setError(errorMessage);

        const assistantMessage: ChatMessage = {
          id: crypto.randomUUID(),
          role: "assistant",
          content: `Sorry, I encountered an error: ${errorMessage}`,
        };

        setMessages((prev) => [...prev, assistantMessage]);
      } finally {
        setIsLoading(false);
      }
    },
    [isLoading, activeSheetContext, conversationHistory, categoriesWithTickers]
  );

  // Execute a sheet action
  const executeSheetAction = useCallback(
    (action: SheetAction): boolean => {
      const service = getSheetsAIService();

      try {
        switch (action.type) {
          case "CREATE_SHEET": {
            const createAction = action as CreateSheetAction;
            const result = service.applySpec(createAction.spec);
            setLastModifiedSheet({
              sheetId: result.sheetId,
              action: "created",
            });
            return true;
          }

          case "EDIT_CELLS": {
            if (!activeSheetId) {
              setError("No sheet selected");
              return false;
            }
            const editAction = action as EditCellsAction;
            const edits = editAction.edits.map((e) => ({
              row: e.row,
              col: e.col,
              value: e.newValue,
            }));
            updateCells(activeSheetId, edits);
            setLastModifiedSheet({
              sheetId: activeSheetId,
              action: `edited ${edits.length} cell(s)`,
            });
            return true;
          }

          case "ADD_ROWS": {
            if (!activeSheetId) {
              setError("No sheet selected");
              return false;
            }
            const addRowsAction = action as AddRowsAction;

            // Get current sheet to analyze column patterns
            const currentSheet = sheets.find(s => s.id === activeSheetId);
            if (!currentSheet) {
              setError("Sheet not found");
              return false;
            }

            // Analyze existing columns to detect LIVE() metric patterns
            const columnMetrics: (string | null)[] = [];
            const headers = currentSheet.data[0] || [];
            for (let colIdx = 0; colIdx < headers.length; colIdx++) {
              // Look at existing data rows to find LIVE() formulas
              let metric: string | null = null;
              for (let rowIdx = 1; rowIdx < currentSheet.data.length; rowIdx++) {
                const cell = currentSheet.data[rowIdx]?.[colIdx] || "";
                const match = cell.match(/=LIVE\s*\(\s*"[^"]+"\s*,\s*"([^"]+)"\s*\)/i);
                if (match && match[1]) {
                  metric = match[1];
                  break;
                }
              }
              columnMetrics.push(metric);
            }

            // Detect ticker column
            const tickerCol = findTickerColumn(currentSheet.data, knownTickers);

            // Sort rows by position (low to high) for sequential insertion
            const sortedRows = [...addRowsAction.rows].sort(
              (a, b) => a.afterRow - b.afterRow
            );

            // The AI sends afterRow values that represent DESIRED FINAL POSITIONS
            // When AI sends sequential values (5, 6, 7...), it wants rows at those sequential positions
            // We should NOT apply cumulative offsets for sequential inserts
            //
            // The only case where we need offsets is when AI sends the SAME position multiple times
            // e.g., afterRow: 5, 5, 5 means "insert 3 rows all after position 5"
            const insertsAtSamePosition = new Map<number, number>();

            for (const row of sortedRows) {
              // Only add offset for DUPLICATE positions (inserting multiple rows at same spot)
              // For sequential positions, the AI has already calculated the correct final positions
              const duplicateOffset = insertsAtSamePosition.get(row.afterRow) || 0;
              const adjustedAfterRow = row.afterRow + duplicateOffset;
              addRow(activeSheetId, adjustedAfterRow);
              const newRowIdx = adjustedAfterRow + 1;

              // Track that we inserted at this position (for handling duplicates)
              insertsAtSamePosition.set(row.afterRow, duplicateOffset + 1);

              // Determine the ticker for this row
              const rowTicker = row.ticker ||
                (row.values && row.values[tickerCol] ? String(row.values[tickerCol]).toUpperCase() : null);

              // Set values for the new row, auto-populating LIVE() formulas
              for (let colIdx = 0; colIdx < headers.length; colIdx++) {
                const providedValue = row.values?.[colIdx];

                if (providedValue !== null && providedValue !== undefined) {
                  // Use the provided value
                  updateCell(activeSheetId, newRowIdx, colIdx, String(providedValue));
                } else if (rowTicker && columnMetrics[colIdx]) {
                  // Auto-populate LIVE() formula based on column pattern
                  updateCell(
                    activeSheetId,
                    newRowIdx,
                    colIdx,
                    `=LIVE("${rowTicker}", "${columnMetrics[colIdx]}")`
                  );
                }
              }
            }
            setLastModifiedSheet({
              sheetId: activeSheetId,
              action: `added ${addRowsAction.rows.length} row(s)`,
            });
            return true;
          }

          case "ADD_COLUMNS": {
            if (!activeSheetId) {
              setError("No sheet selected");
              return false;
            }
            const addColsAction = action as AddColumnsAction;

            // Get initial sheet data for analysis
            const initialSheet = sheets.find(s => s.id === activeSheetId);
            if (!initialSheet) {
              setError("Sheet not found");
              return false;
            }

            // Use centralized ticker column detection with token catalog
            const tickerCol = findTickerColumn(initialSheet.data, knownTickers);
            const numRows = initialSheet.data.length;

            // Sort columns by position (low to high) for sequential insertion
            const sortedCols = [...addColsAction.columns].sort(
              (a, b) => a.afterCol - b.afterCol
            );

            // The AI sends afterCol values that represent DESIRED FINAL POSITIONS
            // When AI sends sequential values (8, 9, 10...), it wants columns at those sequential positions
            // We should NOT apply cumulative offsets for sequential inserts
            //
            // The only case where we need offsets is when AI sends the SAME position multiple times
            // e.g., afterCol: 5, 5, 5 means "insert 3 columns all after position 5"
            //
            // Track: how many times we've inserted at each unique position
            const insertsAtSamePosition = new Map<number, number>();

            for (const col of sortedCols) {
              // Get fresh sheet data after each insertion to get correct row data
              const freshSheet = useSheetsStore.getState().sheets.find(s => s.id === activeSheetId);
              if (!freshSheet) continue;

              // Only add offset for DUPLICATE positions (inserting multiple columns at same spot)
              // For sequential positions, the AI has already calculated the correct final positions
              const duplicateOffset = insertsAtSamePosition.get(col.afterCol) || 0;
              const adjustedAfterCol = col.afterCol + duplicateOffset;
              addColumn(activeSheetId, adjustedAfterCol);
              const newColIdx = adjustedAfterCol + 1;

              // Track that we inserted at this position (for handling duplicates)
              insertsAtSamePosition.set(col.afterCol, duplicateOffset + 1);

              // Set header
              updateCell(activeSheetId, 0, newColIdx, col.header);

              // If it's a live column, set formulas for data rows
              if (col.liveMetric) {
                for (let rowIdx = 1; rowIdx < numRows; rowIdx++) {
                  const row = freshSheet.data[rowIdx];
                  if (!row) continue;

                  // Skip summary rows - they need formula aggregation, not LIVE()
                  if (isSummaryRow(row)) {
                    // Add an AVERAGE formula for the new column
                    const colLetter = colIndexToLetter(newColIdx);
                    updateCell(
                      activeSheetId,
                      rowIdx,
                      newColIdx,
                      `=AVERAGE(${colLetter}2:${colLetter}${rowIdx})`
                    );
                    if (col.format) {
                      setCellFormat(activeSheetId, rowIdx, newColIdx, col.format);
                    }
                    continue;
                  }

                  // Get ticker from the detected ticker column
                  const ticker = row[tickerCol]?.trim();
                  if (ticker && isLikelyTicker(ticker, knownTickers)) {
                    updateCell(
                      activeSheetId,
                      rowIdx,
                      newColIdx,
                      `=LIVE("${ticker.toUpperCase()}", "${col.liveMetric}")`
                    );
                    if (col.format) {
                      setCellFormat(activeSheetId, rowIdx, newColIdx, col.format);
                    }
                  }
                }
              } else if (col.formula) {
                // Apply formula pattern
                for (let rowIdx = 1; rowIdx < numRows; rowIdx++) {
                  const formula = col.formula.replace(/{row}/g, String(rowIdx + 1));
                  updateCell(activeSheetId, rowIdx, newColIdx, formula);
                  if (col.format) {
                    setCellFormat(activeSheetId, rowIdx, newColIdx, col.format);
                  }
                }
              }
            }
            setLastModifiedSheet({
              sheetId: activeSheetId,
              action: `added ${addColsAction.columns.length} column(s)`,
            });
            return true;
          }

          case "DELETE_ROWS": {
            if (!activeSheetId) {
              setError("No sheet selected");
              return false;
            }
            const deleteRowsAction = action as DeleteRowsAction;
            // Delete in reverse order to maintain indices
            const sortedDelRows = [...deleteRowsAction.rowIndices].sort(
              (a, b) => b - a
            );
            for (const row of sortedDelRows) {
              deleteRow(activeSheetId, row);
            }
            setLastModifiedSheet({
              sheetId: activeSheetId,
              action: `deleted ${deleteRowsAction.rowIndices.length} row(s)`,
            });
            return true;
          }

          case "DELETE_COLUMNS": {
            if (!activeSheetId) {
              setError("No sheet selected");
              return false;
            }
            const deleteColsAction = action as DeleteColumnsAction;
            // Delete in reverse order to maintain indices
            const sortedDelCols = [...deleteColsAction.colIndices].sort(
              (a, b) => b - a
            );
            for (const col of sortedDelCols) {
              deleteColumn(activeSheetId, col);
            }
            setLastModifiedSheet({
              sheetId: activeSheetId,
              action: `deleted ${deleteColsAction.colIndices.length} column(s)`,
            });
            return true;
          }

          // Non-modifying actions don't need execution
          case "ANALYZE_SHEET":
          case "TEXT_RESPONSE":
            return true;

          default:
            return false;
        }
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : "Failed to execute action";
        setError(errorMessage);
        return false;
      }
    },
    [
      activeSheetId,
      sheets,
      knownTickers,
      updateCell,
      updateCells,
      setCellFormat,
      addRow,
      deleteRow,
      addColumn,
      deleteColumn,
    ]
  );

  // Confirm and execute all pending actions
  const confirmActions = useCallback(() => {
    if (!pendingResponse) return;

    let allSuccessful = true;
    const modifyingActions = pendingResponse.actions.filter(isModifyingAction);

    for (const action of modifyingActions) {
      if (!executeSheetAction(action)) {
        allSuccessful = false;
        break;
      }
    }

    if (allSuccessful) {
      setPendingResponse(null);

      const confirmMessage: ChatMessage = {
        id: crypto.randomUUID(),
        role: "assistant",
        content:
          modifyingActions.length > 0
            ? "Changes applied successfully. What else would you like to do?"
            : "Got it! What else can I help with?",
      };
      setMessages((prev) => [...prev, confirmMessage]);
    }
  }, [pendingResponse, executeSheetAction]);

  // Discard pending actions
  const discardActions = useCallback(() => {
    setPendingResponse(null);

    const discardMessage: ChatMessage = {
      id: crypto.randomUUID(),
      role: "assistant",
      content: "No changes made. What else can I help with?",
    };
    setMessages((prev) => [...prev, discardMessage]);
  }, []);

  // Execute single action
  const executeAction = useCallback(
    (actionIndex: number) => {
      if (!pendingResponse || !pendingResponse.actions[actionIndex]) return;

      const action = pendingResponse.actions[actionIndex];
      if (executeSheetAction(action)) {
        // Remove executed action from pending
        const remainingActions = pendingResponse.actions.filter(
          (_, i) => i !== actionIndex
        );
        if (remainingActions.length === 0) {
          setPendingResponse(null);
        } else {
          setPendingResponse({ ...pendingResponse, actions: remainingActions });
        }
      }
    },
    [pendingResponse, executeSheetAction]
  );

  // Skip single action
  const skipAction = useCallback(
    (actionIndex: number) => {
      if (!pendingResponse) return;

      const remainingActions = pendingResponse.actions.filter(
        (_, i) => i !== actionIndex
      );
      if (remainingActions.length === 0) {
        setPendingResponse(null);
      } else {
        setPendingResponse({ ...pendingResponse, actions: remainingActions });
      }
    },
    [pendingResponse]
  );

  // Refresh context manually (with live values)
  const refreshSheetContext = useCallback(() => {
    if (activeSheet) {
      fetchLiveValuesForSheet(activeSheet).then((liveCache) => {
        liveCacheRef.current = liveCache;
        setActiveSheetContext(buildSheetContext(activeSheet, liveCache, knownTickers));
      });
    }
  }, [activeSheet, fetchLiveValuesForSheet, knownTickers]);

  // Reset all state
  const reset = useCallback(() => {
    setMessages([]);
    setPendingResponse(null);
    setLastModifiedSheet(null);
    setError(null);
    setConversationHistory([]);
    setIsLoading(false);
  }, []);

  // Clear just conversation
  const clearConversation = useCallback(() => {
    setMessages([]);
    setConversationHistory([]);
  }, []);

  // ─────────────────────────────────────────────────────────────────────────────
  // Legacy Compatibility
  // ─────────────────────────────────────────────────────────────────────────────

  // Extract pending spec from CREATE_SHEET action if present
  const pendingSpec = useMemo(() => {
    if (!pendingResponse) return null;
    const createAction = pendingResponse.actions.find(
      (a) => a.type === "CREATE_SHEET"
    ) as CreateSheetAction | undefined;
    return createAction?.spec || null;
  }, [pendingResponse]);

  // Legacy confirm sheet (maps to confirmActions)
  const confirmSheet = useCallback(() => {
    if (!pendingSpec) return null;

    try {
      const service = getSheetsAIService();
      const result = service.applySpec(pendingSpec);
      setPendingResponse(null);

      const assistantMessage: ChatMessage = {
        id: crypto.randomUUID(),
        role: "assistant",
        content: `Sheet "${result.sheet.name}" has been created! You can find it in your Sheets page.`,
      };
      setMessages((prev) => [...prev, assistantMessage]);

      return result;
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "Failed to create sheet";
      setError(errorMessage);
      return null;
    }
  }, [pendingSpec]);

  // Legacy discard sheet
  const discardSheet = useCallback(() => {
    discardActions();
  }, [discardActions]);

  // Legacy modify pending spec
  const modifyPendingSpec = useCallback(
    async (modification: string) => {
      await sendMessage(modification);
    },
    [sendMessage]
  );

  // ─────────────────────────────────────────────────────────────────────────────
  // Memoized State and Actions
  // ─────────────────────────────────────────────────────────────────────────────

  const state: SheetsState = useMemo(
    () => ({
      messages,
      isLoading,
      pendingResponse,
      activeSheetContext,
      lastModifiedSheet,
      error,
      hasCapability,
      conversationHistory,
      // Legacy
      pendingSpec,
      lastCreatedSheet: lastModifiedSheet
        ? {
            sheetId: lastModifiedSheet.sheetId,
            sheet: sheets.find((s) => s.id === lastModifiedSheet.sheetId) as Sheet,
          }
        : null,
    }),
    [
      messages,
      isLoading,
      pendingResponse,
      activeSheetContext,
      lastModifiedSheet,
      error,
      hasCapability,
      conversationHistory,
      pendingSpec,
      sheets,
    ]
  );

  const actions: SheetsActions = useMemo(
    () => ({
      sendMessage,
      confirmActions,
      discardActions,
      executeAction,
      skipAction,
      refreshSheetContext,
      reset,
      clearConversation,
      // Legacy
      confirmSheet,
      discardSheet,
      modifyPendingSpec,
    }),
    [
      sendMessage,
      confirmActions,
      discardActions,
      executeAction,
      skipAction,
      refreshSheetContext,
      reset,
      clearConversation,
      confirmSheet,
      discardSheet,
      modifyPendingSpec,
    ]
  );

  return [state, actions];
}

export default useSheetsMode;
